import { useState } from "react";
import { Button, ConfigProvider, Flex, Input, Table } from "antd";
import MenuActions from "../../../../../../../../atom/MenuActions";

import { isWindowReadonly } from "../../../../helper";
import {
  isTypeAffiliateAdvisor,
  isTypeLevelWealthAdvisor,
  isTypeProPortalAdvisor,
  isTypeProPortalAffiliateAdvisor,
  isPermissionReadonly,
} from "../../../../../super_admin/helper";
import { getCurrencyValue } from "../../../../../../../../utils/helper/general";
import { getProposalAum } from "../../../../../proposal/helper";

import { SearchOutlined } from "@ant-design/icons";
import { isOrganizationBx } from "../../../../../../../../utils/helper/specialized";

const SectionHome = ({
  handleActionArchiveLead,
  handleActionEditLead,
  handleActionMoveLeadToClient,
  handleActionRTQ,
  handleClientNameClick,
  handleExportLeads,
  handleImportLeadsClick,
  handleNewLeadModalOpen,
  listLeads,
  loading,
  onClickProposalCreate,
  onClickProposalOpen,
  organization,
  permissions,
  userType,
}) => {
  const [searchedText, setSearchedText] = useState("");

  const columns = [
    {
      filteredValue: [searchedText],
      onFilter: (value, record) =>
        String(record.firstName).toLowerCase().includes(value.toLowerCase()) ||
        String(record.lastName).toLowerCase().includes(value.toLowerCase()) ||
        String(record.email).toLowerCase().includes(value.toLowerCase()),
      render: allValues => (
        <a
          className="pointer"
          onClick={() => handleClientNameClick(allValues.email)}
        >
          <b>
            {allValues.firstName} {allValues.lastName}
          </b>
        </a>
      ),
      sorter: (a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`
        ),
      title: "Name",
    },
    {
      hidden: isTypeLevelWealthAdvisor(userType),
      render: allValues => (
        <a href={`mailto:${allValues.email}`}>{allValues.email}</a>
      ),
      sorter: (a, b) => a.email.localeCompare(b.email),
      title: "Email",
    },
    {
      align: "right",
      className: "assets-column",
      hidden:
        isTypeProPortalAdvisor(userType) ||
        isTypeAffiliateAdvisor(userType) ||
        isTypeProPortalAffiliateAdvisor(userType),
      render: allValues =>
        getCurrencyValue(
          allValues.valueMap?.leadInitial?.assets ??
            allValues.valueMap?.guideInitialData?.assets
        ),
      title: "Initial Investable Asset",
    },
    {
      align: "right",
      hidden:
        isTypeProPortalAdvisor(userType) ||
        isTypeAffiliateAdvisor(userType) ||
        isTypeProPortalAffiliateAdvisor(userType),
      render: allValues =>
        allValues.valueMap && getProposalAum(allValues.valueMap.productMap),
      title: "Proposal AUM",
    },
    {
      align: "center",
      hidden:
        isTypeProPortalAdvisor(userType) ||
        isTypeAffiliateAdvisor(userType) ||
        isTypeProPortalAffiliateAdvisor(userType),
      render: allValues => (
        <Flex justify="center">
          {allValues.valueMap?.productMap ? (
            <Button
              onClick={() => onClickProposalOpen(allValues.email)}
              shape="round"
              size="small"
            >
              View Proposal
            </Button>
          ) : (
            <Button
              disabled={
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads)
              }
              onClick={() => onClickProposalCreate(allValues.email)}
              shape="round"
              size="small"
              type="primary"
            >
              Create Proposal
            </Button>
          )}
        </Flex>
      ),
      title: "Proposal Action",
    },
    {
      className: "actions-menu-table-cell",
      render: allValues => (
        <MenuActions
          items={[
            {
              disabled:
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads),
              key: "movetoClient",
              label: "Move to Client",
              onClick: () => handleActionMoveLeadToClient(allValues.email),
            },
            {
              disabled:
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads),
              key: "editLead",
              label: "Edit Lead",
              onClick: () => handleActionEditLead(allValues.email),
            },
            {
              disabled:
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads),
              hidden: isOrganizationBx(organization),
              key: "rtq",
              label: "RTQ",
              onClick: () => handleActionRTQ(allValues.email),
            },
            {
              type: "divider",
            },
            {
              disabled:
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads),
              key: "archiveLead",
              label: <span style={{ color: "red" }}>Archive</span>,
              onClick: () => handleActionArchiveLead(allValues.email),
            },
          ].filter(it => !it.hidden)}
        />
      ),
      width: 40,
    },
  ].filter(it => !it.hidden);

  return (
    <div style={{ padding: "16px 32px" }}>
      <header>
        <h3>Leads</h3>
      </header>
      <main>
        <Flex
          align="center"
          justify="space-between"
          style={{ marginBottom: 16 }}
        >
          <Input
            onChange={event => setSearchedText(event.target.value)}
            placeholder="Search my leads..."
            style={{
              maxWidth: 200,
              height: 36,
              borderRadius: 25,
            }}
            suffix={<SearchOutlined />}
          />

          <Flex gap={12}>
            <Button
              disabled={
                !listLeads?.length ||
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads)
              }
              onClick={handleExportLeads}
              shape="round"
              type="text"
            >
              Export Leads
            </Button>
            <Button
              disabled={
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads)
              }
              onClick={handleImportLeadsClick}
              shape="round"
              type="text"
            >
              Import Leads
            </Button>
            <Button
              disabled={
                isWindowReadonly() ||
                isPermissionReadonly(permissions?.manageLeads)
              }
              onClick={handleNewLeadModalOpen}
              shape="round"
              type="primary"
            >
              New Lead
            </Button>
          </Flex>
        </Flex>
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerColor: "1E1919",
              },
            },
          }}
        >
          <Table
            columns={columns}
            dataSource={listLeads?.map((it, index) => ({ ...it, key: index }))}
            loading={loading}
            pagination={false}
            rowHoverable={false}
            size="small"
          />
        </ConfigProvider>
      </main>
    </div>
  );
};

export default SectionHome;
